import useTranslator from "../../hooks/useTranslator";
import { convertCurrencyFormat } from "../../helper/common";
import { Badge, Button, Checkbox, Drawer, Icon, Tabs } from "../design";
import "./IncomePanel.css";

/**
 * Side drawer shell shared by the payslip / tax / other income drawers.
 * crumbs: [{ label, onClick }] — the last one is the current page.
 * Back arrow before the title, breadcrumb under it, Total + action in the
 * footer.
 */
const IncomePanel = ({
  isOpen,
  onClose,
  onBack,
  title,
  crumbs = [],
  total = 0,
  actionLabel,
  onAction,
  // own row at the top of the footer (e.g. the Sum / Mean switch)
  footerExtra = null,
  children,
}) => {
  const { _t } = useTranslator();

  return (
    <Drawer
      open={Boolean(isOpen)}
      onClose={onClose}
      size="md"
      closeLabel={_t("close")}
      title={
        <span className="incomePanel__title">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={_t("back")}
            iconStart={<Icon name="chevronLeft" size={16} />}
            onClick={onBack ?? onClose}
          />
          {title}
        </span>
      }
      description={
        crumbs.length > 0 ? (
          <span className="incomePanel__crumbs">
            {crumbs.map((c, i) =>
              i === crumbs.length - 1 ? (
                <span key={i} className="incomePanel__crumbCurrent">
                  {c.label}
                </span>
              ) : (
                <span key={i} className="incomePanel__crumbItem">
                  <Button variant="link" size="sm" onClick={c.onClick}>
                    {c.label}
                  </Button>
                  <Icon name="chevronRight" size={14} />
                </span>
              )
            )}
          </span>
        ) : null
      }
      footer={
        <div className="incomePanel__footerWrap">
          {footerExtra}
          <div className="incomePanel__footer">
            <div className="incomePanel__total">
              <span className="incomePanel__totalLabel">
                {_t("totalWeUse")}
              </span>
              <span className="incomePanel__totalValue">
                €{convertCurrencyFormat(total)}
              </span>
            </div>
            <Button size="lg" onClick={onAction ?? onClose}>
              {actionLabel ?? _t("done")}
            </Button>
          </div>
        </div>
      }
    >
      {children}
    </Drawer>
  );
};

export default IncomePanel;

export const IncomePanelLatest = () => {
  const { _t } = useTranslator();
  return <Badge tone="ink">{_t("latest")}</Badge>;
};

const ViewLink = ({ url, children }) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="incomePanel__link"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  ) : null;

/**
 * Document row of step 2: checkbox | title + meta | View + Details
 */
export const IncomePanelDoc = ({
  isOn,
  title,
  badges,
  meta,
  onToggle,
  onDetails,
  viewUrl,
}) => {
  const { _t } = useTranslator();
  return (
    <div className={`incomePanel__card ${isOn ? "is-selected" : ""}`}>
      <Checkbox
        checked={Boolean(isOn)}
        onChange={() => onToggle()}
        ariaLabel={title}
      />
      <div
        className="incomePanel__text incomePanel__text--click"
        onClick={onDetails}
      >
        <span className="incomePanel__name">
          {title}
          {badges}
        </span>
        {meta && <span className="incomePanel__meta">{meta}</span>}
      </div>
      <div className="incomePanel__side">
        <ViewLink url={viewUrl}>{_t("view")}</ViewLink>
        <Button
          variant="secondary"
          size="sm"
          iconEnd={<Icon name="chevronRight" size={14} />}
          onClick={onDetails}
        >
          {_t("details")}
        </Button>
      </div>
    </div>
  );
};

/**
 * Figure row of step 3 (checkbox). info = shown for information only.
 */
export const IncomePanelOption = ({
  isOn,
  label,
  sub,
  value,
  onToggle,
  info = false,
}) => (
  <div className={`incomePanel__card ${isOn ? "is-selected" : ""}`}>
    {info ? (
      <div className="incomePanel__text">
        <span className="incomePanel__name">{label}</span>
        {sub && <span className="incomePanel__meta">{sub}</span>}
      </div>
    ) : (
      <Checkbox
        className="incomePanel__check"
        checked={Boolean(isOn)}
        onChange={() => onToggle()}
        label={label}
        description={sub}
      />
    )}
    <span className="incomePanel__value">{value}</span>
  </div>
);

/**
 * "Taken from <document>" box at the top of step 3.
 */
export const IncomePanelSource = ({ title, viewUrl }) => {
  const { _t } = useTranslator();
  return (
    <div className="incomePanel__source">
      <span className="incomePanel__sourceText">
        <span className="incomePanel__meta">{_t("takenFrom")}</span>
        <span className="incomePanel__name">{title}</span>
      </span>
      <ViewLink url={viewUrl}>{_t("openDocument")}</ViewLink>
    </div>
  );
};

/**
 * Sum / Mean switch for the selected items of a component (step 2),
 * own row at the top of the footer: label left, switch right.
 * Only when 2+ items are selected.
 */
export const IncomePanelMode = ({ mode = "sum", onChange, count = 0 }) => {
  const { _t } = useTranslator();
  if (count < 2 || !onChange) return null;
  return (
    <div className="incomePanel__mode">
      <span className="incomePanel__modeLabel">
        {_t(
          "combineSelected",
          "Label of the Sum / Mean switch in the income panels"
        )}
      </span>
      <Tabs
        block
        className="incomePanel__modeSwitch"
        size="sm"
        label={_t(
          "combineSelected",
          "Label above the Sum / Mean switch in the income panels"
        )}
        items={[
          { value: "sum", label: _t("sum") },
          {
            value: "avg",
            label: _t(
              "mean",
              "Mean option of the Sum / Mean switch in the income panels"
            ),
          },
        ]}
        value={mode}
        onChange={onChange}
      />
    </div>
  );
};
